<template>
  <div class="filter-dialog-wrapper">
    <!-- 筛选弹窗 -->
    <el-popover
      placement="bottom-start"
      trigger="manual"
      popper-class="filter-dialog-popper"
      v-model="localVisible"
      width="640"
      :append-to-body="true"
      :z-index="9999"
    >
      <div class="filter-dialog px-20 py-24">
        <!-- 顶部：标题 + 逻辑下拉 -->
        <div class="flex items-center justify-between h-28 mb-16">
          <span class="logic-label text-14 font-semibold text-333">设置筛选条件</span>
          <el-dropdown trigger="click" @command="onLogicCommand" :popper-append-to-body="true" :z-index="12000">
            <span class="logic-trigger flex cursor-pointer gap-8 items-center">
              {{ logicLabel }}
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown" class="logic-dropdown">
              <el-dropdown-item :class="{active: filterLogic==='AND'}" command="AND">
                <div class="logic-item">
                  <span>满足所有条件</span>
                  <i v-if="filterLogic==='AND'" class="el-icon-check check-icon"></i>
                </div>
              </el-dropdown-item>
              <el-dropdown-item :class="{active: filterLogic==='OR'}" command="OR">
                <div class="logic-item">
                  <span>满足任一条件</span>
                  <i v-if="filterLogic==='OR'" class="el-icon-check check-icon"></i>
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>

        <!-- 筛选条件列表 -->
        <div class="filter-conditions mb-24">
          <div 
            v-for="(condition, index) in conditions" 
            :key="condition.id"
            class="filter-row flex items-center gap-8 mb-8">
            <!-- 字段选择 -->
            <el-select 
			  filterable
              v-model="condition.field" 
              placeholder="指定值"
              class="w-240"
              size="small"
              popper-class="filter-select-dropdown"
              :popper-append-to-body="true"
              :z-index="12000"
              @change="onFieldChange(condition, index)"
            >
              <el-option
                v-for="(field, fieldIndex) in mergedFieldOptions"
                :key="`field-${field.value}-${fieldIndex}`"
                :label="field.label"
                :value="field.value"
              />
            </el-select>

            <!-- 操作符选择 -->
            <el-select 
              v-model="condition.operator" 
              placeholder="指定值"
              class="w-100"
              size="small"
              popper-class="filter-select-dropdown"
              :popper-append-to-body="true"
              :z-index="12000"
              @change="onOperatorChange(condition, index)"
            >
              <el-option
                v-for="(op, opIndex) in getOperatorOptions(condition.field)"
                :key="`op-${op.value}-${opIndex}`"
                :label="op.label"
                :value="op.value"
              />
            </el-select>

            <!-- 值输入 -->
            <div class="value-input w-240">
              <!-- 为空/不为空操作符时，显示禁用状态 -->
              <el-input
                v-if="condition.operator === 'IS_NULL' || condition.operator === 'IS_NOT_NULL'"
                value="无需输入值"
                disabled
                size="small"
                class="w-240"
              />
              
              <!-- 文本输入框 - STRING类型 -->
              <el-input
                v-else-if="getInputType(condition.field, condition.operator) === 'text'"
                v-model="condition.value"
                placeholder="请输入文本"
                clearable
                size="small"
                class="w-240"
              />
              
              <!-- 数字输入框 - NUMBER类型 -->
              <el-input-number
                v-else-if="getInputType(condition.field, condition.operator) === 'number'"
                v-model="condition.value"
                placeholder="请输入数字"
                :precision="0"
                controls-position="right"
                size="small"
                class="w-240"
              />
              
              <!-- 日期选择器 - DATE类型 -->
              <el-date-picker
                v-else-if="getInputType(condition.field, condition.operator) === 'date'"
                :key="`date-${condition.id}-${condition.operator}`"
                v-model="condition.value"
                type="date"
                placeholder="请选择日期"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                size="small"
                class="w-240 filter-select"
              />
              
              <!-- 日期区间选择器 - DATE类型且操作符为BETWEEN -->
              <el-date-picker
                v-else-if="getInputType(condition.field, condition.operator) === 'daterange'"
                :key="`daterange-${condition.id}-${condition.operator}`"
                v-model="condition.value"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                size="small"
                class="date-range filter-select"
                popper-class="filter-date-picker-popper"
                :append-to-body="true"
                :z-index="30000"
              />
              
              <!-- 多选下拉框 - ENUM类型 -->
              <el-select
                v-else-if="getInputType(condition.field, condition.operator) === 'enum'"
                v-model="condition.value"
                placeholder="请选择"
                clearable
                multiple
                collapse-tags
                filterable
                size="small"
                class="w-240"
                popper-class="filter-select-dropdown filter-select-multiple"
                :popper-append-to-body="true"
                :z-index="12000"
              >
                <el-option
                  v-for="(option, optionIndex) in getSelectOptions(condition.field)"
                  :key="`option-${option.value}-${optionIndex}`"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
              
              <!-- 普通下拉选择 -->
              <el-select
                v-else
                v-model="condition.value"
                placeholder="请选择"
                clearable
                size="small"
                class="w-240"
                popper-class="filter-select-dropdown"
                :popper-append-to-body="true"
                :z-index="12000"
              >
                <el-option
                  v-for="(option, optionIndex) in getSelectOptions(condition.field)"
                  :key="`option-${option.value}-${optionIndex}`"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </div>
            <!-- 删除按钮 -->
            <div class="remove-btn ml-4" @click="removeCondition(index)">
              <i class="el-icon-remove-outline text-16 color-label font-medium"></i>
            </div>
          </div>
        </div>
        <!-- 添加条件按钮 -->
        <div class="flex items-center justify-between">
          <div @click="addCondition" class="flex items-center gap-8 cursor-pointer">
            <i class="el-icon-plus color-primary-light f16 font-medium"></i>
            <span class="text-14 font-medium color-label">添加筛选条件</span>
          </div>
          <!-- 底部按钮 -->
          <div class="flex flex-end">
            <el-button @click="handleReset" class="reset-btn" size="small">重置</el-button>
            <el-button type="primary" class="bg-primary-light" @click="handleFilter" size="small" :loading="loading">筛选</el-button>
          </div>
        </div>
        
      </div>

      <!-- 触发元素插槽：manual 模式下需在点击时主动打开弹窗 -->
      <div slot="reference" @click="onReferenceClick">
        <slot></slot>
      </div>
    </el-popover>
  </div>
</template>

<script>
// 静态导入避免HMR问题
import { orgApi } from '@/api/setting/org'

export default {
  name: 'FilterDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    fieldOptions: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    // 是否有激活的筛选条件
    hasActiveFilters: {
      type: Boolean,
      default: false
    },
    // 父组件传入的获取字段函数（返回Promise）
    fieldsFetcher: {
      type: Function,
      default: null
    },
    // 筛选配置
    filterConfig: {
      type: Object,
      default: () => ({
        fieldsApi: '',
        listApi: '',
        pageKey: ''
      })
    }
  },
  computed: {
    logicLabel() {
      return this.filterLogic === 'AND' ? '满足所有条件' : '满足任一条件'
    },
    // 合并字段选项
    mergedFieldOptions() {
      return this.internalFieldOptions.length > 0 ? this.internalFieldOptions : this.fieldOptions
    }
  },
  data() {
    return {
      localVisible: this.value,
      filterLogic: 'AND',
      conditions: [
        {
          id: Date.now(),
          field: '',
          operator: '',
          value: ''
        }
      ],
      // 内部字段选项
      internalFieldOptions: [],
      // 触发元素引用
      triggerElement: null,
      // z-index监控器
      zIndexObserver: null,
      // 操作符配置 - 根据后端Operator枚举
      operatorMap: {
        STRING: [
          { label: '等于', value: 'EQUAL' },
          { label: '不等于', value: 'NOT_EQUAL' },
          { label: '包含', value: 'LIKE' },
          { label: '不包含', value: 'NOT_LIKE' },
          { label: '为空', value: 'IS_NULL' },
          { label: '不为空', value: 'IS_NOT_NULL' }
        ],
        NUMBER: [
          { label: '等于', value: 'EQUAL' },
          { label: '不等于', value: 'NOT_EQUAL' },
          { label: '大于', value: 'GREATER_THAN' },
          { label: '大于等于', value: 'GREATER_THAN_OR_EQUAL' },
          { label: '小于', value: 'LESS_THAN' },
          { label: '小于等于', value: 'LESS_THAN_OR_EQUAL' },
          { label: '为空', value: 'IS_NULL' },
          { label: '不为空', value: 'IS_NOT_NULL' }
        ],
        DATE: [
          { label: '等于', value: 'EQUAL' },
          { label: '不等于', value: 'NOT_EQUAL' },
          { label: '大于', value: 'GREATER_THAN' },
          { label: '大于等于', value: 'GREATER_THAN_OR_EQUAL' },
          { label: '小于', value: 'LESS_THAN' },
          { label: '小于等于', value: 'LESS_THAN_OR_EQUAL' },
          { label: '在范围内', value: 'BETWEEN' },
          { label: '为空', value: 'IS_NULL' },
          { label: '不为空', value: 'IS_NOT_NULL' }
        ],
        ENUM: [
          { label: '属于', value: 'IN' },
          { label: '不属于', value: 'NOT_IN' }
        ]
      },
      // 字段类型配置
      fieldTypeMap: {},
      // 下拉选项配置
      selectOptionsMap: {},
      clickOutHandler: null
    }
  },
  watch: {
    value(val) {
      this.localVisible = val
    },
    localVisible(val) {
      this.$emit('input', val)
      if (val && this.internalFieldOptions.length === 0) {
        this.loadFieldOptions()
      }
      if (val) {
        this.$nextTick(() => {
          this.repositionDatePickers()
          this.forceSetZIndex()
          this.startZIndexWatcher()
        })
        this.bindClickOutToClose()
      } else {
        this.stopZIndexWatcher()
        this.unbindClickOutToClose()
      }
    }
  },
  beforeDestroy() {
    this.stopZIndexWatcher()
    this.unbindClickOutToClose()
  },
  methods: {
    // manual 模式下点击「筛选」按钮时打开弹窗
    onReferenceClick() {
      if (!this.localVisible) this.localVisible = true
    },
    bindClickOutToClose() {
      this.unbindClickOutToClose()
      const handler = (e) => {
        if (!this.localVisible) return
        const el = e.target
        if (!el || typeof el.closest !== 'function') return
        if (el.closest('.filter-dialog-wrapper') ||
            el.closest('.filter-dialog-popper') ||
            el.closest('.el-select-dropdown') ||
            el.closest('.el-picker-panel') ||
            el.closest('.el-dropdown-menu')) return
        this.localVisible = false
      }
      this.clickOutHandler = handler
      this.$nextTick(() => {
        setTimeout(() => document.addEventListener('click', handler), 0)
      })
    },
    unbindClickOutToClose() {
      if (this.clickOutHandler) {
        document.removeEventListener('click', this.clickOutHandler)
        this.clickOutHandler = null
      }
    },
    // 加载字段选项
    async loadFieldOptions() {
      try {
        let response = null
        if (this.fieldsFetcher) {
          response = await this.fieldsFetcher()
        } else if (this.filterConfig && this.filterConfig.fieldsApi) {
          const apiModule = await this.getApiModule()
          
          // 根据 fieldsApi 的值来调用对应的方法
          const fieldsApi = this.filterConfig.fieldsApi
          let fetchFn = null
          
          if (fieldsApi.includes('addressApi.getAddressFields')) {
            fetchFn = apiModule.getAddressFields
          } else if (fieldsApi.includes('ticketTypeApi.getTicketTypeFields')) {
            fetchFn = apiModule.getTicketTypeFields
          } else if (fieldsApi.includes('getDetailFields')) {
            fetchFn = () => apiModule.getDetailFields()
          }  else if (fieldsApi.includes('getAnnouncementFields')) {
            fetchFn = apiModule.getAnnouncementFields
          } else if (fieldsApi.includes('getUserFields')) {
            fetchFn = apiModule.getUserFields
          } else if (fieldsApi.includes('getSystemLogFields')) {
            fetchFn = apiModule.getSystemLogFields
          } else if (fieldsApi.includes('getHelpFields')) {
            fetchFn = apiModule.getHelpFields
          } else if (fieldsApi.includes('getDataArchiveFields')) {
            fetchFn = apiModule.getDataArchiveFields
          } else if (fieldsApi.includes('driverInfoApi.getDriverFields')) {
            fetchFn = apiModule.getDriverFields
          } else if (fieldsApi.includes('driverReviewApi.getReviewFields')) {
            fetchFn = apiModule.getReviewFields
          } else if (fieldsApi.includes('getDetailFields')) {
            // 明细筛选条件（包括调整调度明细）
            fetchFn = apiModule.getDetailFields
          } else if (fieldsApi.includes('addressApi') && fieldsApi.includes('getFields')) {
            // 地址筛选字段，需传 dataType：1 仓库 2 购货
            const dataType = this.filterConfig.dataType != null ? this.filterConfig.dataType : 1
            fetchFn = () => apiModule.getFields(dataType)
          } else if (fieldsApi.includes('getFields')) {
            fetchFn = apiModule.getFields
          } else if (fieldsApi.includes('vehicleTypeApi')) {
            fetchFn = apiModule.getVehicleTypeFields
          } else if (fieldsApi.includes('blacklistApi.getBlacklistFields')) {
            fetchFn = apiModule.getBlacklistFields
          } else if (fieldsApi.includes('productManagementApi.getFields')) {
            fetchFn = apiModule.getFields
          } else if (fieldsApi.includes('newOrderApi.getFields')) {
            fetchFn = apiModule.getFields
          } else if (fieldsApi.includes('freightSettlementApi.getFields')) {
            fetchFn = apiModule.getFields
          } else if (fieldsApi.includes('ticketProcessingApi.getFields')) {
            fetchFn = apiModule.getFields
          } 
          
          if (typeof fetchFn !== 'function') {
            throw new Error(`未找到获取字段的函数: ${fieldsApi}`)
          }
          response = await fetchFn()
        } else {
          return
        }
        
        if (response.code === 200) {
          this.internalFieldOptions = (response.data || []).map(item => ({
            label: item.name,
            value: item.code,
            type: item.type,
            dataType: item.dataType,
            operators: item.operators,
            options: item.options
          }))
        } else {
          this.setDefaultFieldOptions()
        }
      } catch (error) {
        this.setDefaultFieldOptions()
      }
    },
    
    // 根据配置获取API模块
    async getApiModule() {
      const { fieldsApi } = this.filterConfig
      
      // 公告模块
      if (fieldsApi.includes('announcementApi')) {
        const { announcementApi } = await import('@/api/setting/Announcement')
        return announcementApi
      }
      // 帮助文档模块
      if (fieldsApi.includes('helpApi')) {
        const { helpApi } = await import('@/api/setting/help')
        return helpApi
      }
      // 系统日志模块
      if (fieldsApi.includes('systemLogApi')) {
        const { systemLogApi } = await import('@/api/setting/SystemLog')
        return systemLogApi
      }
      // 数据档案模块
      if (fieldsApi.includes('dataArchiveApi')) {
        const { dataArchiveApi } = await import('@/api/setting/DataArchive')
        return dataArchiveApi
      }
      // 仓库管理模块 购货地址
      if (fieldsApi.includes('addressApi')) {
        const { addressApi } = await import('@/api/setting/Address')
        return addressApi
      }
      // 组织架构模块
      if (fieldsApi.includes('orgApi')) {
        return orgApi
      }
      // 工单类型模块
      if (fieldsApi.includes('ticketTypeApi')) {
        const { ticketTypeApi } = await import('@/api/setting/TicketType')
        return ticketTypeApi
      }
      // 司机信息模块
      if (fieldsApi.includes('driverInfoApi')) {
        const { driverInfoApi } = await import('@/api/drivermanagement/DriverInfo')
        return driverInfoApi
      }
      // 司机审核模块
      if (fieldsApi.includes('driverReviewApi')) {
        const { driverReviewApi } = await import('@/api/drivermanagement/DriverReview')
        return driverReviewApi
      }
      // 车辆类型模块
      if (fieldsApi.includes('vehicleTypeApi')) {
        const { vehicleTypeApi } = await import('@/api/drivermanagement/VehicleType')
        return vehicleTypeApi
      }
      // 黑名单模块
      if (fieldsApi.includes('blacklistApi')) {
        const { blacklistApi } = await import('@/api/drivermanagement/Blacklist')
        return blacklistApi
      }
      // 产品管理模块
      if (fieldsApi.includes('productManagementApi')) {
        const { productManagementApi } = await import('@/api/ordermanagement/ProductManagement')
        return productManagementApi
      }
      // 新订单模块
      if (fieldsApi.includes('newOrderApi')) {
        const { newOrderApi } = await import('@/api/ordermanagement/NewOrder')
        return newOrderApi
      }
      // 工单处理模块
      if (fieldsApi.includes('ticketProcessingApi')) {
        const { ticketProcessingApi } = await import('@/api/ordermanagement/TicketProcessing')
        return ticketProcessingApi
      }
      // 运费结算模块
      if (fieldsApi.includes('freightSettlementApi')) {
        const { freightSettlementApi } = await import('@/api/ordermanagement/FreightSettlement')
        return freightSettlementApi
      }
      throw new Error(`未找到对应的API模块: ${fieldsApi}`)
    },
    
    // 设置默认字段选项
    setDefaultFieldOptions() {
      this.internalFieldOptions = [
        { label: '名称', value: 'name', type: 'STRING', operators: ['EQUAL', 'LIKE'] },
        { label: '创建时间', value: 'createTime', type: 'DATE', operators: ['EQUAL', 'BETWEEN'] }
      ]
    },
    
    onLogicCommand(cmd) {
      this.filterLogic = cmd
    },
    
    // 取消筛选
    cancelFilter() {
      this.localVisible = false
    },
    
    // 确定筛选
    confirmFilter() {
      const filterData = {
        logic: this.filterLogic,
        conditions: this.conditions.filter(condition => 
          condition.field && condition.operator && condition.value
        )
      }
      this.$emit('filter', filterData)
    },
    
    // 添加筛选条件
    addCondition() {
      this.conditions.push({
        id: Date.now(),
        field: '',
        operator: '',
        value: ''
      })
    },
    
    // 删除筛选条件
    removeCondition(index) {
      if (this.conditions.length > 1) {
        this.conditions.splice(index, 1)
      }
    },
    
    // 字段变化处理
    onFieldChange(condition, index) {
      // 重置操作符和值
      condition.operator = ''
      // 根据字段类型重置值形态
      const fieldTypeAfter = this.getFieldType(condition.field)
      if (fieldTypeAfter === 'enum') {
        condition.value = []
      } else if (fieldTypeAfter === 'date' || fieldTypeAfter === 'daterange') {
        condition.value = ''
      } else {
        condition.value = ''
      }
      
      // 设置默认操作符 - 优先使用接口返回的操作符
      const fieldOption = this.mergedFieldOptions.find(option => option.value === condition.field)
      if (fieldOption) {
        // 如果接口返回了操作符，使用接口的操作符
        if (fieldOption.operators && Array.isArray(fieldOption.operators) && fieldOption.operators.length > 0) {
          condition.operator = fieldOption.operators[0]
        }
        // 如果接口没有返回操作符，使用默认操作符
        else if (fieldOption.type) {
          const operators = this.operatorMap[fieldOption.type] || this.operatorMap.STRING
          if (operators.length > 0) {
            condition.operator = operators[0].value
            // 若是枚举默认 IN，确保值为数组
            if (fieldOption.type === 'ENUM' && (condition.operator === 'IN' || condition.operator === 'NOT_IN')) {
              condition.value = Array.isArray(condition.value) ? condition.value : []
            }
          }
        }
      }
    },
    
    // 操作符变化处理
    onOperatorChange(condition, index) {
      // 当操作符为"为空"或"不为空"时，清空值
      if (condition.operator === 'IS_NULL' || condition.operator === 'IS_NOT_NULL') {
        condition.value = ''
      }
      // 当操作符为BETWEEN时，需要调整输入类型为daterange
      else if (condition.operator === 'BETWEEN') {
        const fieldType = this.getFieldType(condition.field)
        if (fieldType === 'date') {
          condition.value = Array.isArray(condition.value) ? condition.value : []
        }
      } else if (condition.operator === 'IN' || condition.operator === 'NOT_IN') {
        // IN/NOT_IN 需要数组
        condition.value = Array.isArray(condition.value) ? condition.value : []
      } else {
        // 其他操作符使用标量
        if (Array.isArray(condition.value)) condition.value = ''
      }
      
      // 由于使用了key属性，Vue会自动重新渲染组件
      // 只需要确保日期选择器正确定位
      this.$nextTick(() => {
        this.repositionDatePickers()
      })
    },
    
    // 获取字段类型 - 根据后端QueryFieldMetadata的type字段
    getFieldType(field) {
      // 首先从接口返回的字段选项中查找
      const fieldOption = this.mergedFieldOptions.find(option => option.value === field)
      if (fieldOption) {
        // 根据后端返回的 type 字段直接映射
        const typeMap = {
          'STRING': 'text',
          'NUMBER': 'number', 
          'DATE': 'date',
          'ENUM': 'enum'
        }
        return typeMap[fieldOption.type] || 'text'
      }
      
      // 如果接口数据中没有，使用配置的类型映射
      if (this.fieldTypeMap[field]) {
        return this.fieldTypeMap[field]
      }
      
      // 最后根据字段名推断类型
      const fieldName = field.toLowerCase()
      if (fieldName.includes('date') || fieldName.includes('time')) {
        return 'date'
      } else if (fieldName.includes('id') || fieldName.includes('count') || fieldName.includes('num')) {
        return 'number'
      } else if (fieldName.includes('status') || fieldName.includes('type')) {
        return 'enum'
      }
      return 'text'
    },
    
    // 获取输入类型 - 根据字段类型和操作符动态判断
    getInputType(field, operator = '') {
      const fieldType = this.getFieldType(field)
      
      // 如果操作符是BETWEEN，且字段类型是DATE，则使用daterange
      if (operator === 'BETWEEN' && fieldType === 'date') {
        return 'daterange'
      }
      
      return fieldType
    },
    
    // 获取操作符选项 - 根据后端Operator枚举
    getOperatorOptions(field) {
      // 首先从接口返回的字段选项中查找
      const fieldOption = this.mergedFieldOptions.find(option => option.value === field)
      if (fieldOption && fieldOption.operators && Array.isArray(fieldOption.operators) && fieldOption.operators.length > 0) {
        // 将接口返回的操作符转换为组件需要的格式
        const operatorMap = {
          'EQUAL': '等于',
          'NOT_EQUAL': '不等于',
          'LIKE': '包含', 
          'NOT_LIKE': '不包含',
          'GREATER_THAN': '大于',
          'GREATER_THAN_OR_EQUAL': '大于等于',
          'LESS_THAN': '小于',
          'LESS_THAN_OR_EQUAL': '小于等于',
          'BETWEEN': '在范围内',
          'IN': '属于',
          'NOT_IN': '不属于',
          'IS_NULL': '为空',
          'IS_NOT_NULL': '不为空'
        }
        return fieldOption.operators.map(op => ({
          label: operatorMap[op] || op,
          value: op
        }))
      }
      
      // 如果接口数据中没有operators字段，根据字段类型使用对应的操作符
      const fieldOption2 = this.mergedFieldOptions.find(option => option.value === field)
      if (fieldOption2 && fieldOption2.type && (!fieldOption2.operators || fieldOption2.operators.length === 0)) {
        return this.operatorMap[fieldOption2.type] || this.operatorMap.STRING
      }
      
      // 如果接口返回了operators数组，但数组为空，也不应该使用默认操作符
      if (fieldOption2 && fieldOption2.operators && fieldOption2.operators.length === 0) {
        return []
      }
      
      // 最后使用默认的STRING操作符
      return this.operatorMap.STRING
    },
    
    // 获取下拉选项
    getSelectOptions(field) {
      // 首先从接口返回的字段选项中查找
      const fieldOption = this.mergedFieldOptions.find(option => option.value === field)
      if (fieldOption && fieldOption.options) {
        // 如果接口返回了选项列表，使用接口数据
        return fieldOption.options
      }
      
      // 如果接口数据中没有，使用配置的选项映射
      return this.selectOptionsMap[field] || []
    },
    
    // 重置
    handleReset() {
      this.filterLogic = 'AND'
      this.conditions = [
        {
          id: Date.now(),
          field: '',
          operator: '',
          value: ''
        }
      ]
      
      // 触发重置事件，通知父组件重新请求列表
      this.$emit('reset')
      
      // 关闭弹窗
      this.localVisible = false
    },
    
    // 重置筛选条件（不触发事件，不关闭弹窗）
    resetFilterConditions() {
      this.filterLogic = 'AND'
      this.conditions = [
        {
          id: Date.now(),
          field: '',
          operator: '',
          value: ''
        }
      ]
    },
    
    // 筛选
    handleFilter() {
      // 过滤出有效的查询条件
      const validConditions = this.conditions.filter(condition => {
        // 基本验证：字段和操作符必须存在
        if (!condition.field || !condition.operator) {
          return false
        }
        
        // 对于"为空"和"不为空"操作符，只需要字段和操作符有值
        if (condition.operator === 'IS_NULL' || condition.operator === 'IS_NOT_NULL') {
          return true
        }
        
        // 对于其他操作符，需要验证值不为空
        if (Array.isArray(condition.value)) {
          return condition.value.length > 0
        }
        return condition.value !== '' && condition.value !== null && condition.value !== undefined
      })
      
      // 构建筛选数据，只传递有效的查询条件
      const filterData = validConditions.length > 0 ? {
        logic: this.filterLogic,
        conditions: validConditions.map(condition => {
          let value = condition.value
          
          // 对于"为空"和"不为空"操作符，不需要传递值
          if (condition.operator === 'IS_NULL' || condition.operator === 'IS_NOT_NULL') {
            value = null
          } else {
            // 处理多选数组：将数组转换为逗号分隔的字符串
            if (Array.isArray(value) && value.length > 0) {
              value = value.join(',')
            }
            // 处理日期范围：将数组转换为逗号分隔的字符串
            else if (Array.isArray(value) && value.length === 2) {
              value = value.join(',')
            }
            // 如果value为空（null、undefined、空字符串、空数组），都传成空字符串
            else if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
              value = ''
            }
          }
          
          return {
            field: condition.field,
            operator: condition.operator,
            value: value,
            dataType: this.getDataType(condition.field)
          }
        })
      } : null
      
      this.$emit('filter', filterData)
      
      // 筛选完成后关闭 popover
      this.localVisible = false
    },
    
    // 获取数据类型 - 根据后端QueryFieldMetadata的dataType字段
    getDataType(field) {
      // 首先从接口返回的字段选项中查找
      const fieldOption = this.mergedFieldOptions.find(option => option.value === field)
      if (fieldOption && fieldOption.dataType) {
        return fieldOption.dataType
      }
      
      // 如果接口数据中没有，根据字段类型映射
      const fieldType = this.getFieldType(field)
      const typeMap = {
        text: 'STRING',
        number: 'NUMBER',
        date: 'DATE',
        daterange: 'DATE',
        enum: 'ENUM'
      }
      return typeMap[fieldType] || 'STRING'
    },
    
    // 切换显示状态
    toggleVisible() {
      this.localVisible = !this.localVisible
    },
    
    // 关闭对话框
    handleClose() {
      this.localVisible = false
    },
    
    // 设置字段类型配置
    setFieldTypeConfig(config) {
      this.fieldTypeMap = { ...this.fieldTypeMap, ...config }
    },
    
    // 设置下拉选项配置
    setSelectOptionsConfig(config) {
      this.selectOptionsMap = { ...this.selectOptionsMap, ...config }
    },
    
    // 设置触发元素
    setTriggerElement(element) {
      this.triggerElement = element
    },
    
    // 重新定位日期选择器
    repositionDatePickers() {
      // 简化处理，只确保z-index正确
      this.$nextTick(() => {
        const datePickers = document.querySelectorAll('.filter-date-picker-popper .el-picker-panel')
        datePickers.forEach(panel => {
          if (panel.style) {
            panel.style.zIndex = '30000'
          }
        })
      })
    },
    
    // 强制设置所有弹出层的z-index
    forceSetZIndex() {
      this.$nextTick(() => {
        // 设置所有筛选相关的弹出层z-index
        const selectDropdowns = document.querySelectorAll('.filter-select-dropdown')
        selectDropdowns.forEach(dropdown => {
          if (dropdown.style) {
            dropdown.style.zIndex = '12000'
          }
        })
        
        const logicDropdowns = document.querySelectorAll('.logic-dropdown')
        logicDropdowns.forEach(dropdown => {
          if (dropdown.style) {
            dropdown.style.zIndex = '12000'
          }
        })
        
        // 设置所有el-popper的z-index
        const poppers = document.querySelectorAll('.el-popper')
        poppers.forEach(popper => {
          if (popper.style && (popper.classList.contains('filter-select-dropdown') || popper.classList.contains('logic-dropdown'))) {
            popper.style.zIndex = '12000'
          }
        })

        // 单选/区间 日期选择器面板层级强制最高
        const datePanels = document.querySelectorAll('.filter-date-picker-popper, .filter-date-picker-popper .el-picker-panel')
        datePanels.forEach(panel => {
          if (panel && panel.style) {
            panel.style.zIndex = '13000'
          }
        })
      })
    },
    
    // 启动z-index监控
    startZIndexWatcher() {
      if (this.zIndexObserver) {
        this.zIndexObserver.disconnect()
      }
      
      this.zIndexObserver = new MutationObserver(() => {
        this.forceSetZIndex()
      })
      
      // 监听body下的所有变化
      this.zIndexObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      })
    },
    
    // 停止z-index监控
    stopZIndexWatcher() {
      if (this.zIndexObserver) {
        this.zIndexObserver.disconnect()
        this.zIndexObserver = null
      }
    },
    
  }
}
</script>

<style scoped>
.w-240{
  width: 240px !important;
}
.filter-dialog-wrapper {
  display: inline-block;
  position: relative;
}

/* 筛选弹窗样式 */
.logic-trigger {
  color: rgba(96, 98, 102, 1);
  padding: 4px 10px;
  background: #f5f7fa;
  border-radius: 6px;
}
.remove-btn { cursor: pointer; }
</style>

<style>
/* 筛选弹窗样式 */
.filter-dialog-popper {
  z-index: 10000 !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-radius: 8px !important;
}

/* 下拉菜单美化 */
.logic-dropdown{
  box-sizing: border-box;
  width: 172px !important;
  padding: 8px !important;
  border-radius: 4px;
  background: rgba(255, 255, 255, 1);
  box-shadow: 0px 1px 6px  rgba(0, 0, 0, 0.12);
  z-index: 10002 !important;
}
.logic-dropdown .el-dropdown-menu__item {
  height: 40px;
  padding: 0px 18px 0px 12px;  
  font-size: 14px;
  font-weight: 500;
  color: rgba(48, 49, 51, 1);
}
.logic-dropdown .el-dropdown-menu__item:hover {
  background: rgba(245, 247, 249, 1);
  color: rgba(48, 49, 51, 1)!important;
}
.logic-dropdown .logic-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.logic-dropdown .check-icon {
  color: rgba(42, 117, 247, 1);
  font-weight: 600;
}
/* 全局样式 - 用于 popover */
.filter-dialog-popper { 
  padding: 0 !important; 
  border-radius: 4px !important;
  overflow: hidden !important;
  box-shadow: 0px 3px 14px 2px rgba(0, 0, 0, 0.05) !important;

}

/* 优化 popover 内的滚动条 */
.filter-dialog-popper .filter-dialog {
  max-height: 70vh;
  overflow-y: auto;
}

/* 控制日期输入宽度更紧凑 */
.filter-dialog .el-date-editor .el-range-input{
  width: 33%;
}
.filter-dialog .el-range-separator{
  width: 14% !important;
  text-align: center !important;
}
.filter-dialog .date-range .el-range-editor.el-input__inner,.filter-dialog .el-date-editor--daterange.el-input__inner{
  width: 240px !important;
}
.reset-btn:hover{
  background: #fff !important;
  color: #606266 !important;
  border: 1px solid #DCDFE6 !important;
}

/* 筛选弹框内所有下拉框的层级 */
:deep(.filter-select-dropdown) {
  z-index: 12000 !important;
}

/* 筛选弹框内所有下拉菜单的层级 */
:deep(.filter-dialog-popper .el-dropdown-menu) {
  z-index: 12000 !important;
}


.logic-dropdown,.filter-select ,.filter-select-dropdown,.date-range,.el-date-picker{
  z-index: 12000 !important;
}

/* 日期选择器弹出层样式 */
.filter-date-picker-popper {
  z-index: 30000 !important;
}

.filter-select-multiple .el-select .el-tag{
  z-index: 12000 !important;
  max-width: 80% !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
